import {ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {Router} from '@angular/router';
import {IDcpGlobalErrorMessage} from '@app/core/models/interfaces/error';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {
  DcpEntryGroup,
  DcpGlobalMessageService,
  JsonService,
  LocalStorageService,
  PendingConfiguratorRequestService
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CartDetailsComponent} from '@spartacus/cart/base/components';
import {CartConfigService} from '@spartacus/cart/base/core';
import {ActiveCartFacade, PromotionResult, SelectiveCartFacade,} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {AuthService, CmsComponent, EventService, GlobalMessageType, RoutingService,} from '@spartacus/core';
import {CmsComponentData} from '@spartacus/storefront';
import {isEmpty} from 'lodash-es';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import {OrderItemService} from '../checkout/order-item/services/order-item.service';
import {CheckoutErrorHandlingService} from '../checkout/services/checkout-error-handling.service';
import {TemplateView} from '../model/template-view.model';
import {DcpCartService} from '../product/services/dcp-cart.service';
import {EddlCartViewEvent} from '../services/analytics/EDDL/events/eddl-cart-view-event';
import {DateService} from '../services/date/date.service';
import {DcpActiveCartService} from '../services/dcp-active-cart.service';
import {OutdatedCartMessage} from './outdated-cart-message/outdated-cart-message.component';

export interface CartComponentData extends CmsComponent {
  json?: { message: string };
}

@UntilDestroy()
@Component({
  selector: 'dcp-cart',
  templateUrl: './cart.component.html',
})
export class CartComponent
  extends CartDetailsComponent
  implements OnInit, OnDestroy {
  public cartEntries$: Observable<OrderEntry[]>;
  public outdatedCartMessage$: Observable<String>;
  public cartEntryGroups: DcpEntryGroup[] = [];
  public view = TemplateView;
  public hideCartContent: boolean = false;
  public cartEntryGroupsSubscription: Subscription = new Subscription();
  public cartSubscription: Subscription = Subscription.EMPTY;
  public isVisibleDisposableBlock: boolean = true;
  public isNeedToShowOutdatedCartMessage: boolean = false;
  public isDataInitialized: boolean = false;
  private isCartViewEDDLReady: boolean = true;
  private hasPendingRequest = false;

  constructor(
    public dcpActiveCartService: DcpActiveCartService,
    public activeCartService: ActiveCartFacade,
    public authService: AuthService,
    public eventService: EventService,
    public routingService: RoutingService,
    public dcpGlobalMessageService: DcpGlobalMessageService,
    protected checkoutErrorHandlingService: CheckoutErrorHandlingService,
    private cartService: DcpCartService,
    private orderItemService: OrderItemService,
    private dateService: DateService,
    private cmsData: CmsComponentData<CartComponentData>,
    private jsonService: JsonService,
    protected localStorageService: LocalStorageService,
    private orderEntryService: DcpOrderEntryService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private router: Router,
    private pendingConfiguratorRequestService: PendingConfiguratorRequestService,
    activeCartFacade: ActiveCartFacade,
    selectiveCartFacade: SelectiveCartFacade,
    cartConfigService: CartConfigService,
    private cdr: ChangeDetectorRef
  ) {
    super(
      activeCartFacade,
      selectiveCartFacade,
      authService,
      routingService,
      cartConfigService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.cartEntries$ = this.activeCartService.getEntries();
    this.subscribeToEntryGroups();
    this.cart$ = this.checkoutErrorHandlingService.cartRequestErrorWrapper(
      this.cart$
    );
    this.manageCartSubscription();
    this.removeOutdatedPromoMessages();
    this.displayPromotionsMessages();
    this.isVisibleDisposableMessages();
    this.subscribeToOutdatedCartMessage();
    this.intOutdatedCartMessageState();
    this.writePriceTooltipData();
    this.isDataInitialized = false;
    this.pendingConfiguratorRequestService
      .hasPendingRequests()
      .pipe(
        tap(pending => this.hasPendingRequest = pending),
        untilDestroyed(this),
        filter((hasPendingRequests) => !hasPendingRequests),
        take(1)
      )
      .subscribe(() => {
        this.hasPendingRequest = false;
        this.isDataInitialized = true;
        this.cdr.detectChanges();
      });
    this.localStorageService.setItem('lastPage', this.router.url);
  }

  ngOnDestroy(): void {
    this.orderItemService.clearOrderItemCollapsedState();
    this.cartEntryGroupsSubscription.unsubscribe();
    this.cartSubscription.unsubscribe();
  }

  showIndicator(isVisible: boolean) {
    this.isDataInitialized = !isVisible && !this.hasPendingRequest;
    this.cdr.detectChanges();
  }

  /**
   * Subscribes or unsubscribes to cart changes when the edit modal is opened.
   * If the cart is refreshed while the modal is opened the UI will be refreshed.
   * While refreshing the UI, the modal component will be destroyed and the modal will be closed.
   * This can be prevented by unsubscribing while the modal is opened.
   */
  manageCartSubscription() {
    this.cartSubscription = this.cartService.cartSubscriptionStatus
      .pipe(untilDestroyed(this))
      .subscribe((s) => {
        if (s === 'subscribe') {
          this.subscribeToEntryGroups();
        } else {
          this.cartEntryGroupsSubscription.unsubscribe();
        }
      });
  }

  /**
   * The cart is outdated when modified time year
   * is not equal current year or cart was modified in previous Quarter
   */
  isCartOutdated(): Observable<boolean> {
    return this.cart$.pipe(
      untilDestroyed(this),
      filter((cart) => !!cart.expirationTime),
      take(1),
      map((cart) => cart.expirationTime),
      map(
        (expirationTime) =>
          expirationTime.getTime() < this.dateService.getCurrentDate().getTime()
      )
    );
  }

  intOutdatedCartMessageState(): void {
    this.isCartOutdated()
      .pipe(untilDestroyed(this), take(1))
      .subscribe(
        (isCartOutdated) =>
          (this.isNeedToShowOutdatedCartMessage = isCartOutdated)
      );
  }

  private subscribeToEntryGroups() {
    // Subscription.add caused the view not to be updated with current cart data.
    this.cartEntryGroupsSubscription.unsubscribe();
    this.cartEntryGroupsSubscription = this.dcpActiveCartService
      .getEntryGroups()
      .pipe(untilDestroyed(this))
      .subscribe((eg) => {
        this.cartEntryGroups = eg;
        if (this.isCartViewEDDLReady) {
          this.isCartViewEDDLReady = false;
          setTimeout(() => this.eventService.dispatch(new EddlCartViewEvent()));
        }
      });
  }

  /**
   * Confirmation messages are used for both potential and applied promotions
   * Potential promotions can all be put under the same group name
   * Applied promos should be split into 2 groups: appliedCouponsPromotions and appliedAutomaticPromotions
   */
  displayPromotionsMessages(): void {
    this.cart$.pipe(untilDestroyed(this)).subscribe((cart) => {
      const appliedPromos: PromotionResult[] =
        cart.appliedProductPromotions?.concat(
          cart.appliedOrderPromotions ?? []
        );
      appliedPromos?.forEach((promotionResult) => {
        if (
          cart.appliedVouchers?.some((voucher) =>
            promotionResult.promotion?.appliedCouponCode
              ?.toLowerCase()
              ?.includes(voucher.code?.toLowerCase())
          )
        ) {
          this.fireConfirmationMessages(
            promotionResult,
            'appliedCouponsPromotions'
          );
        } else {
          this.fireConfirmationMessages(
            promotionResult,
            'appliedAutomaticPromotions'
          );
        }
      });
      const potentialPromos: PromotionResult[] =
        cart.potentialProductPromotions?.concat(
          cart.potentialOrderPromotions ?? []
        );
      potentialPromos?.forEach((promotionResult) => {
        this.fireConfirmationMessages(promotionResult, 'potentialPromotions');
      });
    });
  }

  /**
   * filter the outdated promo messages and remove them
   */
  removeOutdatedPromoMessages(): void {
    this.cart$.pipe(untilDestroyed(this)).subscribe((cart) => {
      const appliedPromos: PromotionResult[] =
        cart.appliedProductPromotions?.concat(
          cart.appliedOrderPromotions ?? []
        );
      this.dcpGlobalMessageService
        .getActiveMessages()
        .pipe(untilDestroyed(this), take(1))
        .subscribe((activeMessages) => {
          activeMessages
            .filter(
              (msg) =>
                appliedPromos?.length === 0 ||
                appliedPromos.some((promo) => msg.message !== promo.description)
            )
            .forEach((msg) =>
              this.dcpGlobalMessageService.removeMessage(msg.id)
            );
        });
    });
  }

  private fireConfirmationMessages(promo: PromotionResult, group: string) {
    this.dcpGlobalMessageService.addMessage(
      {
        message: promo.description,
        type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        group,
      },
      this.constructor.name,
      'errorGlobal'
    );
  }

  private subscribeToOutdatedCartMessage(): void {
    this.outdatedCartMessage$ = this.cmsData.data$.pipe(
      untilDestroyed(this),
      take(1),
      map((cartData: CartComponentData) => cartData.json),
      map(
        (json) =>
          this.jsonService.parseSafely(json?.message) as OutdatedCartMessage
      ),
      map((message) => message.outdatedCartMessage)
    );
  }

  private isVisibleDisposableMessages() {
    const disposableMessages$: Observable<IDcpGlobalErrorMessage[]> =
      this.dcpGlobalMessageService.getActiveMessages();
    const disposableMessageTmpl$: Observable<TemplateRef<any>> =
      this.dcpGlobalMessageService.getActiveTemplate();

    combineLatest([disposableMessages$, disposableMessageTmpl$])
      .pipe(untilDestroyed(this))
      .subscribe(([disposableMessages, disposableMessageTmpl]) => {
        this.isVisibleDisposableBlock =
          !isEmpty(disposableMessages) || !!disposableMessageTmpl;
      });
  }

  private writePriceTooltipData() {
    this.cartEntries$.pipe(untilDestroyed(this)).subscribe((entries) => {
      const localStorageMap = new Map<string, string[]>();
      const nonConfigurableEntries = entries.filter(
        (entry) => !entry.product?.configurable
      );

      nonConfigurableEntries.forEach((entry) => {
        //get the key == product code
        const currentProductCode = this.dcpProductUtilitiesService.getProductId(
          entry.product
        );
        // get the content
        const priceTooltipItem =
          this.orderEntryService.populatePriceTooltipDataItem(entry);

        if (localStorageMap.has(currentProductCode)) {
          localStorageMap.get(currentProductCode).push(priceTooltipItem);
        } else {
          localStorageMap.set(currentProductCode, [priceTooltipItem]);
        }
        this.localStorageService.setItem(
          currentProductCode,
          JSON.stringify(localStorageMap.get(currentProductCode))
        );
      });
    });
  }
}
